import jsPDF from "jspdf";
import "jspdf-autotable";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// --- FUNÇÕES UTILITÁRIAS ---
const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    // Ajuste para timezone local para evitar problemas de dia anterior
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() + userTimezoneOffset).toLocaleDateString("pt-BR");
};
const formatCurrency = (value) => parseFloat(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatNumber = (value, fractionDigits = 2) => {
    const num = parseFloat(value || 0);
    if (isNaN(num)) return 'N/A';
    return num.toLocaleString('pt-BR', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });
};

// =================================================================================
//   RELATÓRIO PDF
// =================================================================================
export const generateInventoryPDFReport = ({ selectedData, logo, chartImage, imovel }) => {
    if (selectedData.length === 0) return;

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const primaryColor = '#1e8449';
    const mainTextColor = '#1a2e35';
    const secondaryTextColor = '#64748b';
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let startY = 0;
    const imovelDesc = imovel?.descricao || `Imóvel ID: ${imovel?.id || 'N/A'}`;
    const cleanImovelDesc = imovelDesc.replace(/[^a-z0-9]/gi, '_').substring(0, 30);

    // --- Função para criar cabeçalho e rodapé ---
    const headerFooter = (data) => {
        // Cabeçalho
        doc.addImage(logo, 'PNG', margin, 12, 35, 12);
        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor(mainTextColor);
        doc.text("Relatório de Inventário Florestal", pageWidth - margin, 18, { align: 'right' });
        
        doc.setFont('Helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(secondaryTextColor);
        doc.text(`Imóvel: ${imovelDesc}`, pageWidth - margin, 24, { align: 'right' });

        doc.setDrawColor(238, 242, 245);
        doc.line(margin, 30, pageWidth - margin, 30);
        // Rodapé
        doc.setFontSize(8);
        doc.setTextColor(secondaryTextColor);
        doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, doc.internal.pageSize.getHeight() - 10);
        doc.text(`Página ${data.pageNumber} de ${doc.internal.getNumberOfPages()}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
    };

    // --- PÁGINA 1: DASHBOARD ---
    headerFooter({ pageNumber: 1 });
    startY = 45;

    // KPIs
    const totalVolume = selectedData.reduce((sum, inv) => sum + parseFloat(inv.volume_total_m3 || 0), 0);
    const totalValor = selectedData.reduce((sum, inv) => sum + parseFloat(inv.valor_total || 0), 0);
    const totalArvores = selectedData.reduce((sum, inv) => sum + parseInt(inv.quantidade_arvores || 0), 0);
    const mediaDiametro = selectedData.reduce((sum, inv) => sum + parseFloat(inv.diametro_medio || 0), 0) / selectedData.length;

    const kpiData = [
        { label: "Volume Total Consolidado", value: `${formatNumber(totalVolume)} m³` },
        { label: "Valor Total Estimado", value: formatCurrency(totalValor) },
        { label: "Média Árvores/ha", value: formatNumber(totalArvores / selectedData.length, 0) },
        { label: "Média Diâmetro", value: `${formatNumber(mediaDiametro)} cm` }
    ];
    
    const cardWidth = (pageWidth - (margin * 2) - 15) / 4; // 4 KPIs
    kpiData.forEach((item, index) => {
        const cardX = margin + (index * (cardWidth + 5));
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(238, 242, 245);
        doc.roundedRect(cardX, startY - 10, cardWidth, 22, 3, 3, 'FD');
        doc.setFont('Helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(secondaryTextColor);
        doc.text(item.label, cardX + 5, startY);
        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(primaryColor);
        doc.text(item.value, cardX + 5, startY + 7);
    });
    startY += 25;

    // Gráfico
    if (chartImage) {
        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(mainTextColor);
        doc.text("Composição de Volume (Consolidado)", margin, startY);
        doc.addImage(chartImage, 'PNG', margin, startY + 5, 80, 80);
    }
    
    // Tabela Resumo
    const summaryHead = [["Nº", "Data", "Vol. (m³)", "Valor (R$)"]];
    const summaryBody = selectedData.map(inv => [
        inv.numero_inventario, formatDate(inv.data),
        formatNumber(inv.volume_total_m3), formatCurrency(inv.valor_total)
    ]);
    doc.autoTable({
        head: summaryHead, body: summaryBody,
        startY: startY,
        margin: { left: margin + 90 },
        theme: "striped",
        headStyles: { fillColor: primaryColor, textColor: 255, font: 'Helvetica', fontStyle: 'bold' },
        styles: { font: 'Helvetica', fontSize: 9 },
    });
    startY = doc.lastAutoTable.finalY + 15;
    
    // --- PÁGINA 2: DETALHAMENTO DE CLASSES ---
    doc.addPage();
    headerFooter({ pageNumber: 2 });
    startY = 40;

    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(mainTextColor);
    doc.text("Detalhamento por Classe Diamétrica (Volume TON)", margin, startY);
    startY += 10;

    const detailedHeadVol = [["Nº Inv.", "Lenha (TON)", "15-20 (TON)", "20-25 (TON)", "25-33 (TON)", "+33 (TON)", "Total (m³)"]];
    const detailedBodyVol = selectedData.map(inv => [
        inv.numero_inventario,
        formatNumber(inv.volume_lenha), formatNumber(inv.volume_15_a_20),
        formatNumber(inv.volume_20_a_25), formatNumber(inv.volume_25_a_33),
        formatNumber(inv.volume_33_acima), formatNumber(inv.volume_total_m3)
    ]);
    doc.autoTable({
        head: detailedHeadVol, body: detailedBodyVol,
        startY: startY, theme: "striped",
        headStyles: { fillColor: primaryColor, textColor: 255, font: 'Helvetica', fontStyle: 'bold' },
        styles: { font: 'Helvetica', fontSize: 9 },
    });
    startY = doc.lastAutoTable.finalY + 15;

    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(mainTextColor);
    doc.text("Detalhamento por Classe (Valor R$)", margin, startY);
    startY += 10;
    
    const detailedHeadVal = [["Nº Inv.", "Lenha", "15-20", "20-25", "25-33", "+33", "Total R$"]];
    const detailedBodyVal = selectedData.map(inv => [
        inv.numero_inventario,
        formatCurrency(inv.valor_lenha), formatCurrency(inv.valor_15_a_20),
        formatCurrency(inv.valor_20_a_25), formatCurrency(inv.valor_25_a_33),
        formatCurrency(inv.valor_33_acima), formatCurrency(inv.valor_total)
    ]);
    doc.autoTable({
        head: detailedHeadVal, body: detailedBodyVal,
        startY: startY, theme: "striped",
        headStyles: { fillColor: primaryColor, textColor: 255, font: 'Helvetica', fontStyle: 'bold' },
        styles: { font: 'Helvetica', fontSize: 9 },
    });

    // --- PÁGINAS SEGUINTES: FICHA COMPLETA ---
    selectedData.forEach((inv, index) => {
        doc.addPage();
        const pageNumber = doc.internal.getNumberOfPages();
        headerFooter({ pageNumber: pageNumber });
        startY = 40;

        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(16);
        doc.setTextColor(primaryColor);
        doc.text(`Ficha do Inventário: ${inv.numero_inventario}`, margin, startY);
        startY += 10;

        const fichaData = [
            ['Data', formatDate(inv.data)],
            ['Qtd. Amostras', formatNumber(inv.quantidade_amostras, 0)],
            ['Árvores/ha', formatNumber(inv.quantidade_arvores, 0)],
            ['Peso Específico', `${formatNumber(inv.peso_kg_m3)} kg/m³`],
            ['Diâmetro Médio', `${formatNumber(inv.diametro_medio)} cm`],
            ['Altura Média', `${formatNumber(inv.altura_media)} m`],
        ];
        
        const fichaClasses = [
            ['Volume Total', `${formatNumber(inv.volume_total_m3)} m³`],
            ['Volume Total', `${formatNumber(inv.volume_total_ton)} TON`],
            ['Valor Total', formatCurrency(inv.valor_total)],
            ['Volume Lenha', `${formatNumber(inv.volume_lenha)} TON`],
            ['Valor Lenha', formatCurrency(inv.valor_lenha)],
            ['Volume 15-20cm', `${formatNumber(inv.volume_15_a_20)} TON`],
            ['Valor 15-20cm', formatCurrency(inv.valor_15_a_20)],
            ['Volume 20-25cm', `${formatNumber(inv.volume_20_a_25)} TON`],
            ['Valor 20-25cm', formatCurrency(inv.valor_20_a_25)],
            ['Volume 25-33cm', `${formatNumber(inv.volume_25_a_33)} TON`],
            ['Valor 25-33cm', formatCurrency(inv.valor_25_a_33)],
            ['Volume +33cm', `${formatNumber(inv.volume_33_acima)} TON`],
            ['Valor +33cm', formatCurrency(inv.valor_33_acima)],
        ];
        
        doc.autoTable({
            head: [['Dados Gerais', '']],
            body: fichaData,
            startY: startY,
            theme: 'grid',
            headStyles: { fillColor: primaryColor, textColor: 255, font: 'Helvetica', fontStyle: 'bold' },
            styles: { font: 'Helvetica', fontSize: 10 },
            columnStyles: { 0: { fontStyle: 'bold', fillColor: '#f8fafc' } }
        });
        
        doc.autoTable({
            head: [['Classes de Volume e Valor', '']],
            body: fichaClasses,
            startY: doc.lastAutoTable.finalY + 10,
            theme: 'grid',
            headStyles: { fillColor: primaryColor, textColor: 255, font: 'Helvetica', fontStyle: 'bold' },
            styles: { font: 'Helvetica', fontSize: 10 },
            columnStyles: { 0: { fontStyle: 'bold', fillColor: '#f8fafc' } }
        });
    });

    doc.save(`Relatorio_Inventario_${cleanImovelDesc}.pdf`);
};

// =================================================================================
//   RELATÓRIO EXCEL (Com ExcelJS)
// =================================================================================
export const generateInventoryExcelReport = async ({ selectedData, imovel }) => {
    if (selectedData.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'SisFlorestal';
    workbook.created = new Date();

    const imovelDesc = imovel?.descricao || `Imóvel ID: ${imovel?.id || 'N/A'}`;
    const cleanImovelDesc = imovelDesc.replace(/[^a-z0-9]/gi, '_').substring(0, 30);

    // --- ESTILOS COMPARTILHADOS ---
    const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF196F3D' } }; // Verde Escuro
    const headerFont = { name: 'Arial', bold: true, color: { argb: 'FFFFFFFF' } }; // Branco
    const titleFont = { name: 'Arial', bold: true, size: 16, color: { argb: 'FF145A32' } };
    const kpiLabelFont = { name: 'Arial', bold: true, color: { argb: 'FF1A2E35' } };
    const kpiValueFont = { name: 'Arial', size: 14, color: { argb: 'FF1E8449' } };

    // --- FORMATOS ---
    const currencyFormat = '"R$"#,##0.00';
    const floatFormat = '#,##0.00';
    const intFormat = '#,##0';

    // =============================================================================
    // ABA 1: RESUMO (DASHBOARD)
    // =============================================================================
    const wsResumo = workbook.addWorksheet('Resumo');

    const totalVolume = selectedData.reduce((sum, inv) => sum + parseFloat(inv.volume_total_m3 || 0), 0);
    const totalValor = selectedData.reduce((sum, inv) => sum + parseFloat(inv.valor_total || 0), 0);
    const totalArvores = selectedData.reduce((sum, inv) => sum + parseInt(inv.quantidade_arvores || 0), 0);
    const mediaDiametro = selectedData.reduce((sum, inv) => sum + parseFloat(inv.diametro_medio || 0), 0) / selectedData.length;

    wsResumo.columns = [
        { width: 35 }, { width: 25 }, { width: 25 }
    ];

    wsResumo.getCell('A1').value = "Relatório de Inventário Florestal";
    wsResumo.getCell('A1').font = titleFont;

    wsResumo.getCell('A2').value = `Imóvel: ${imovelDesc}`;
    wsResumo.getCell('A2').font = { size: 14, color: { argb: 'FF4A5568' } };

    wsResumo.getCell('A4').value = "KPIs Consolidados";
    wsResumo.getCell('A4').font = { bold: true, size: 12, color: { argb: 'FF1A2E35' } };

    const kpiRows = [
        { label: "Inventários Selecionados:", value: selectedData.length, format: intFormat },
        { label: "Volume Total Consolidado:", value: totalVolume, format: floatFormat + ' "m³"' },
        { label: "Valor Total Estimado:", value: totalValor, format: currencyFormat },
        { label: "Média Árvores/ha:", value: totalArvores / selectedData.length, format: intFormat },
        { label: "Média Diâmetro:", value: mediaDiametro, format: floatFormat + ' "cm"' }
    ];

    kpiRows.forEach((kpi, index) => {
        const rowIndex = 5 + index;
        const labelCell = wsResumo.getCell(`A${rowIndex}`);
        const valueCell = wsResumo.getCell(`B${rowIndex}`);

        labelCell.value = kpi.label;
        labelCell.font = kpiLabelFont;

        valueCell.value = kpi.value;
        valueCell.font = kpiValueFont;
        valueCell.numFmt = kpi.format;
        valueCell.alignment = { horizontal: 'left' };
    });

    wsResumo.getCell('A11').value = "Dados Consolidados por Classe (Soma)";
    wsResumo.getCell('A11').font = { bold: true, size: 12, color: { argb: 'FF1A2E35' } };

    const chartHeaderRow = wsResumo.getRow(12);
    chartHeaderRow.values = ['Classe Diamétrica', 'Volume Total (TON)', 'Valor Total (R$)'];
    chartHeaderRow.eachCell((cell) => {
        cell.fill = headerFill;
        cell.font = headerFont;
        cell.alignment = { horizontal: 'center' };
    });

    const chartDataSource = {
        'Lenha': { volume: 0, valor: 0 }, '15 a 20cm': { volume: 0, valor: 0 },
        '20 a 25cm': { volume: 0, valor: 0 }, '25 a 33cm': { volume: 0, valor: 0 },
        '+33cm': { volume: 0, valor: 0 },
    };
    selectedData.forEach(inv => {
        chartDataSource['Lenha'].volume += parseFloat(inv.volume_lenha || 0);
        chartDataSource['15 a 20cm'].volume += parseFloat(inv.volume_15_a_20 || 0);
        chartDataSource['20 a 25cm'].volume += parseFloat(inv.volume_20_a_25 || 0);
        chartDataSource['25 a 33cm'].volume += parseFloat(inv.volume_25_a_33 || 0);
        chartDataSource['+33cm'].volume += parseFloat(inv.volume_33_acima || 0);
        chartDataSource['Lenha'].valor += parseFloat(inv.valor_lenha || 0);
        chartDataSource['15 a 20cm'].valor += parseFloat(inv.valor_15_a_20 || 0);
        chartDataSource['20 a 25cm'].valor += parseFloat(inv.valor_20_a_25 || 0);
        chartDataSource['25 a 33cm'].valor += parseFloat(inv.valor_25_a_33 || 0);
        chartDataSource['+33cm'].valor += parseFloat(inv.valor_33_acima || 0);
    });

    Object.entries(chartDataSource).forEach(([classe, data]) => {
        const row = wsResumo.addRow([classe, data.volume, data.valor]);
        row.getCell(2).numFmt = floatFormat;
        row.getCell(3).numFmt = currencyFormat;
    });

    // =============================================================================
    // ABA 2: DETALHAMENTO
    // =============================================================================
    const wsDetailed = workbook.addWorksheet('Detalhamento');

    wsDetailed.columns = [
        { header: 'Número Inventário', key: 'num_inv', width: 20 },
        { header: 'Data', key: 'data', width: 15 },
        { header: 'Qtd Amostras', key: 'qtd_amostras', width: 15 },
        { header: 'Árvores/ha', key: 'arvores_ha', width: 15 },
        { header: 'Peso (kg/m³)', key: 'peso', width: 15 },
        { header: 'Diâmetro Médio (cm)', key: 'diametro', width: 20 },
        { header: 'Altura Média (m)', key: 'altura', width: 18 },
        { header: 'Volume Total (m³)', key: 'vol_m3', width: 18 },
        { header: 'Volume Total (TON)', key: 'vol_ton', width: 18 },
        { header: 'Valor Total (R$)', key: 'valor_total', width: 20 },
        { header: 'Volume Lenha (TON)', key: 'vol_lenha', width: 20 },
        { header: 'Valor Lenha (R$)', key: 'val_lenha', width: 20 },
        { header: 'Vol. 15-20 (TON)', key: 'vol_15_20', width: 18 },
        { header: 'Val. 15-20 (R$)', key: 'val_15_20', width: 18 },
        { header: 'Vol. 20-25 (TON)', key: 'vol_20_25', width: 18 },
        { header: 'Val. 20-25 (R$)', key: 'val_20_25', width: 18 },
        { header: 'Vol. 25-33 (TON)', key: 'vol_25_33', width: 18 },
        { header: 'Val. 25-33 (R$)', key: 'val_25_33', width: 18 },
        { header: 'Vol. +33 (TON)', key: 'vol_33', width: 18 },
        { header: 'Val. +33 (R$)', key: 'val_33', width: 18 },
    ];

    wsDetailed.getRow(1).eachCell((cell) => {
        cell.fill = headerFill;
        cell.font = headerFont;
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    selectedData.forEach(inv => {
        const rowData = {
            num_inv: inv.numero_inventario,
            data: new Date(inv.data),
            qtd_amostras: parseFloat(inv.quantidade_amostras || 0),
            arvores_ha: parseFloat(inv.quantidade_arvores || 0),
            peso: parseFloat(inv.peso_kg_m3 || 0),
            diametro: parseFloat(inv.diametro_medio || 0),
            altura: parseFloat(inv.altura_media || 0),
            vol_m3: parseFloat(inv.volume_total_m3 || 0),
            vol_ton: parseFloat(inv.volume_total_ton || 0),
            valor_total: parseFloat(inv.valor_total || 0),
            vol_lenha: parseFloat(inv.volume_lenha || 0),
            val_lenha: parseFloat(inv.valor_lenha || 0),
            vol_15_20: parseFloat(inv.volume_15_a_20 || 0),
            val_15_20: parseFloat(inv.valor_15_a_20 || 0),
            vol_20_25: parseFloat(inv.volume_20_a_25 || 0),
            val_20_25: parseFloat(inv.valor_20_a_25 || 0),
            vol_25_33: parseFloat(inv.volume_25_a_33 || 0),
            val_25_33: parseFloat(inv.valor_25_a_33 || 0),
            vol_33: parseFloat(inv.volume_33_acima || 0),
            val_33: parseFloat(inv.valor_33_acima || 0),
        };

        const row = wsDetailed.addRow(rowData);

        row.getCell('data').numFmt = 'dd/mm/yyyy';
        ['qtd_amostras', 'arvores_ha'].forEach(key => row.getCell(key).numFmt = intFormat);
        ['peso', 'diametro', 'altura', 'vol_m3', 'vol_ton', 'vol_lenha', 'vol_15_20', 'vol_20_25', 'vol_25_33', 'vol_33']
            .forEach(key => row.getCell(key).numFmt = floatFormat);
        ['valor_total', 'val_lenha', 'val_15_20', 'val_20_25', 'val_25_33', 'val_33']
            .forEach(key => row.getCell(key).numFmt = currencyFormat);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `Relatorio_Inventarios_${cleanImovelDesc}.xlsx`);
};